import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { trackPageview } from "@/lib/analytics";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import Download from "@/pages/Download";
import Dashboard from "@/pages/Dashboard";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Teams from "@/pages/Teams";
import CreateTeam from "@/pages/CreateTeam";
import JoinTeam from "@/pages/JoinTeam";
import TeamDashboard from "@/pages/TeamDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Profile from "@/pages/Profile";
import Terms from "@/pages/Terms";
import Help from "@/pages/Help";
import HelpArticle from "@/pages/HelpArticle";
import UseCases from "@/pages/UseCases";
import Glossary from "@/pages/Glossary";
import Security from "@/pages/Security";
import CookieConsent from "@/components/CookieConsent";

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/receive" element={<Download />} />
      <Route path="/r/:code" element={<Download />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/teams" element={<Teams />} />
      <Route path="/teams/create" element={<CreateTeam />} />
      <Route path="/teams/join" element={<JoinTeam />} />
      <Route path="/teams/:team_id" element={<TeamDashboard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/help" element={<Help />} />
      <Route path="/help/:slug" element={<HelpArticle />} />
      <Route path="/use-cases" element={<UseCases />} />
      <Route path="/glossary" element={<Glossary />} />
      <Route path="/security" element={<Security />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <main className="max-w-3xl mx-auto px-6 py-32 text-center">
      <div className="font-mono text-[#FF3366] text-6xl mb-4">404</div>
      <div className="small-caps mb-2">Lost in the grid</div>
      <p className="text-black/60">That page doesn’t exist.</p>
    </main>
  );
}

function App() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-black">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Chivo:wght@400;700;800;900&family=IBM+Plex+Sans:wght@400;500;600&family=JetBrains+Mono:wght@500;700&display=swap"
        rel="stylesheet"
      />
      <BrowserRouter>
        <HelmetProvider>
          <AuthProvider>
            <Navbar />
            <div className="flex-1">
              <AppRouter />
            </div>
            <Footer />
            <Toaster theme="light" position="bottom-right" />
            <CookieConsent />
          </AuthProvider>
        </HelmetProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
